/**
 * Impersonation state + sessionStorage glue. Owned by the admin shell;
 * read by:
 *   - api/http.ts        — request interceptor uses IMP token when active
 *   - ImpersonationBanner — renders the bright-red strip
 *   - api/sso.ts         — mints / stops via SSO admin endpoints
 *
 * Storage: sessionStorage (NOT localStorage). Per-tab is the correct
 * scoping — closing the tab ends the session; opening a fresh tab is the
 * admin's normal session, not the operator's.
 */
import { setImpersonationAccessToken } from '../api/http';

const STORAGE_KEY = 'cb_impersonation';

export interface ImpersonationState {
  accessToken: string;
  expiresAt: string;      // ISO
  targetUserId: string;
  /** Human-readable label shown in the banner (e.g. "Mungun (mungun@x)"). */
  targetLabel: string;
}

type Listener = (state: ImpersonationState | null) => void;
const listeners = new Set<Listener>();

function emit(state: ImpersonationState | null) {
  listeners.forEach((fn) => {
    try { fn(state); } catch { /* noop */ }
  });
}

function read(): ImpersonationState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ImpersonationState;
    if (!parsed?.accessToken || !parsed.expiresAt) return null;
    if (new Date(parsed.expiresAt).getTime() <= Date.now()) {
      // Expired — drop without firing token slot change (request interceptor
      // already lost the token because writes set both together).
      sessionStorage.removeItem(STORAGE_KEY);
      setImpersonationAccessToken(null);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export const impersonation = {
  /** Current state, or null if no active session. Returns null and clears storage if the token has naturally expired. */
  get(): ImpersonationState | null {
    return read();
  },

  /** Start a session — persists to storage + publishes the token to the API client. */
  set(state: ImpersonationState): void {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    setImpersonationAccessToken(state.accessToken);
    emit(state);
  },

  /** End the session locally. Does NOT call the server stop endpoint — banner / caller does that. */
  clear(): void {
    sessionStorage.removeItem(STORAGE_KEY);
    setImpersonationAccessToken(null);
    emit(null);
  },

  /** Subscribe to state changes. Returns an unsubscribe. */
  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  },

  /**
   * Bootstrap on app load. If sessionStorage has a non-expired token,
   * republish it to the API client so the first request after a refresh
   * uses the impersonation token. Called once by AuthContext at mount.
   */
  bootstrap(): void {
    const current = read();
    if (current) {
      setImpersonationAccessToken(current.accessToken);
    }
  },
};
